import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const REASONS = [["casse", "Casse"], ["chute", "Chute / renversé"], ["brule", "Brûlé / raté"], ["erreur", "Erreur de préparation"], ["autre", "Autre"]];

// Déclaration de perte depuis un ticket cuisine : trace sur la commande + sortie de stock si recette.
export default function KitchenLossDialog({ order, onClose }) {
  const [idx, setIdx] = useState(0);
  const [qty, setQty] = useState(1);
  const [reason, setReason] = useState("casse");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const it = order.items[idx];
  const submit = async () => {
    setBusy(true);
    try {
      await api.kitchenLoss(order.id, { item_index: idx, quantity: Number(qty) || 1, reason, note, client_request_id: `${order.id}-${idx}-${Date.now()}` });
      toast.success(`Perte enregistrée : ${qty}× ${it.name}`);
      onClose(true);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Erreur"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent data-testid="kds-loss-dialog" className="max-w-md rounded-3xl bg-[#111F1A] border-[#1B3028] text-stone-100">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-400" /> Déclarer une perte</DialogTitle>
          <DialogDescription className="text-stone-400">T{order.table_number} · {order.order_code || order.order_number}. La perte est tracée dans le journal et déduite du stock si le plat a une fiche recette. Le ticket reste à refaire.</DialogDescription>
        </DialogHeader>
        <label className="text-xs text-stone-400">Plat concerné
          <select data-testid="kds-loss-item" value={idx} onChange={(e) => { setIdx(Number(e.target.value)); setQty(1); }} className="mt-1 w-full bg-[#0A1712] border border-[#1B3028] rounded-xl px-3 py-2.5 text-stone-100 min-h-[44px]">
            {order.items.map((x, i) => <option key={i} value={i}>{x.quantity}× {x.name}</option>)}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs text-stone-400">Quantité perdue
            <input data-testid="kds-loss-qty" type="number" min={1} max={it?.quantity || 1} value={qty} onChange={(e) => setQty(Math.min(it?.quantity || 1, Math.max(1, Number(e.target.value) || 1)))} className="mt-1 w-full bg-[#0A1712] border border-[#1B3028] rounded-xl px-3 py-2.5 text-stone-100 min-h-[44px]" />
          </label>
          <label className="text-xs text-stone-400">Motif
            <select data-testid="kds-loss-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 w-full bg-[#0A1712] border border-[#1B3028] rounded-xl px-3 py-2.5 text-stone-100 min-h-[44px]">
              {REASONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </label>
        </div>
        <input data-testid="kds-loss-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} placeholder="Précision (facultatif)" className="w-full bg-[#0A1712] border border-[#1B3028] rounded-xl px-3 py-2.5 text-sm text-stone-100 min-h-[44px]" />
        <div className="flex gap-2 pt-1">
          <button data-testid="kds-loss-cancel" onClick={() => onClose(false)} className="flex-1 h-12 rounded-full border border-[#1B3028] text-stone-300 font-semibold">Annuler</button>
          <button data-testid="kds-loss-submit" onClick={submit} disabled={busy} className="flex-1 h-12 rounded-full bg-amber-500 text-[#0C1713] font-extrabold disabled:opacity-50">{busy ? "…" : "Enregistrer la perte"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
